import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isDefinedAndNotEmpty, obtenerBaseUrl, UserException } from '@cf-v5-workspace/utils';
import { Observable, Observer, Subscription } from 'rxjs';
import { YeminusApis } from '@cf-v5-workspace/enums';
import Globals from './interfaces/IGlobals';
import { SessionStorageService } from '../services/session-storage.service';
import { IAppSettings } from './interfaces/IAppSettings';

export default class BaseService {
	defaultSettings: IAppSettings;

	public set headers(v: {}) {
		this.defaultHeaders = v;
	}

	public get headers(): {} {
		return this.defaultHeaders;
	}

	private defaultHeaders: {} = {};

	constructor(public http: HttpClient, private sessionStorageService: SessionStorageService) {

	}

	/**
	 * Establece los headers por defecto
	 * @param cancelContentType
	 */
	setDefaultHeaders(cancelContentType: boolean = false) {
		try {
			let global = this.sessionStorageService.obtenerInfoSesion();

			if (global.token) {
				
				this.defaultHeaders['Content-type'] = 'application/json';

				if (cancelContentType) {
					delete this.defaultHeaders['Content-type'];
				}
				this.defaultHeaders['Authorization'] = `${global.token_type} ${global.token}`;
				this.defaultHeaders['id_empresa'] = global.idEmpresa;
				this.defaultHeaders['usuario'] = global.usuario;
				this.defaultHeaders['Throttle-Key'] = '39a3af33665a439c9cb2a80532465eb1';
			}
			global = null;
		} catch (error) {
			console.log(error);
		}
	}

	/**
	 * Consume de un servicio del api y devuelve un observable
	 * @param url url del servicio del api
	 * @param data los parámetros que van en el body de la petición
	 * @param setDefaultHeaders determina si se va a ejecutar la función setDefaultHeaders o no, por defecto siempre se va a ejectuar
	 * @param setDefaultHeaders determina si se va a ejecutar la función setDefaultHeaders o no, por defecto siempre se va a ejectuar
	 */
	consumeService(url: string, data?: any, setDefaultHeaders: boolean = true, cancelContentType: boolean = false): Observable<any> {

		return Observable.create((observer: Observer<any>) => {
			// lee el json de configuración inicial
			const servicio = this.getDefaultSettings().subscribe((defSettings: IAppSettings) => {

				this.defaultSettings = defSettings;
				if (setDefaultHeaders) {
					this.setDefaultHeaders(cancelContentType);
				}
				const postService = this.http.post(this.defaultSettings.base_url + url, data, { headers: this.defaultHeaders })
					.subscribe(
						(response: any) => {
							observer.next(response);
							postService.unsubscribe();
						},
						(error: any) => {
							observer.error(error);
							postService.unsubscribe();
						}
					);
				servicio.unsubscribe();
			});
		});
	}

	/**
	 * Consume de un servicio del api y devuelve un observable
	 * @param url url del servicio del api
	 * @param data los parámetros que van en el body de la petición
	 * @param setDefaultHeaders determina si se va a ejecutar la función setDefaultHeaders o no, por defecto siempre se va a ejectuar
	 * @returns {observable}
	 */
	consumeApiService(
		url: string,
		data?: any,
		yeminusApi: YeminusApis = YeminusApis.ApiYeminus
	): Observable<any> {
		return Observable.create((observer: Observer<any>) => {
			// lee el json de configuración inicial
			this.getDefaultSettings()
				.subscribe((defSettings: IAppSettings) => {
					this.defaultSettings = defSettings;
					let header = this.setHeaders();
					let postService : Subscription = this.http
						.post(obtenerBaseUrl(defSettings.base_url, yeminusApi, defSettings.dev_mode) + url, data, { headers: header })
						.subscribe(
							(response: any) => {
								observer.next(response);
								postService.unsubscribe();
							},
							(error: any) => {
								observer.error(error);
								postService.unsubscribe();
								if (this.defaultHeaders['guid'] != null) {
							}
						}
					);

				});
		});
	}

	/**
	 * Se configuran los headers para que se adecuen a la peticion que recibe .Net 6
	 * @param cancelContentType 
	 * @author Bvelez
	 * @returns 
	 */
	public setHeaders(cancelContentType: boolean = false) {
		let header = new HttpHeaders()
		try {
			let global: Globals;
			global = this.sessionStorageService.obtenerInfoSesion();
			header = header.set('Throttle-Key', '39a3af33665a439c9cb2a80532465eb1');
			if (global.token) {
				header = header.set('Content-type', 'application/json');
				if (cancelContentType) {
					header.delete('Content-type');
				}
				header = header.set('Authorization', `${global.authtoken_type} ${global.authtoken}`);
				header = header.set('id_empresa', global.idEmpresa ||  this.defaultSettings.idEmpresa);
				header = header.set('id_sucursal', '');
				header = header.set('guid',this.sessionStorageService?.getNewGUIDString());


				header = header.set('usuario', this.defaultSettings.usuario);
				
			}
			global = new Globals();
		} catch (error) {
			UserException(
				`Ocurrió un error al establecer los headers`,
				error,
				`${BaseService.name} -> ${this.setDefaultHeaders.name}`
			);
		}
		return header;
	}

	/**
	 * Se loguea con unos datos iniciales y obtiene el token para las peticiones
	 */
	login(idEmpresa?: string): Observable<IAppSettings> {

		// this.clearCookies();
		const headers: HttpHeaders = new HttpHeaders();
		let data: string;

		return Observable.create((observer: Observer<IAppSettings>) => {
			// lee el json de configuración inicial
			const servicio = this.getDefaultSettings().subscribe((defSettings: IAppSettings) => {
				headers['Content-Type'] = 'application/x-www-form-urlencoded';
				headers['Throttle-Key'] = '39a3af33665a439c9cb2a80532465eb1';
				data = 'grant_type=password&username=' + defSettings.usuario + '&password=' + defSettings.password;

				const postService = this.http.post(defSettings.base_url + 'token', data, { headers })
					.subscribe(async (response: any) => {
						// Se guarda el token en la info de sesion
						const globals = new Globals();
						globals.base_url = defSettings.base_url;
						globals.token = response.access_token;
						globals.token_type = response.token_type;
						globals.idEmpresa = idEmpresa;
						globals.usuario = defSettings.usuario;
						this.sessionStorageService.modificarInfoSesion(globals);
						// -------------------------------------
						await this.ObtenerAuthToken(data,defSettings);
						observer.next(response);
						postService.unsubscribe();
					}, (error) => {
						postService.unsubscribe();
						UserException('error', error, `base-service => ${this.login.name}`);
		
					});
				servicio.unsubscribe();
			}, (error) => {
				servicio.unsubscribe();
				UserException('error', error, `base-service => ${this.login.name}`);

			});
		});
	}

	/**
	 * Se realiza conexion a .Net 6. Se obtiene el token
	 * @param data 
	 * @param configuracionGeneral 
	 * @returns 
	 * @author Bvelez
	 */
	ObtenerAuthToken(data: string, configuracionGeneral: IAppSettings): Promise<void>{
		return new Promise((resolve, reject) => {
			const headers = {
				headers: new HttpHeaders({'Content-Type':'application/x-www-form-urlencoded', 
										'Throttle-Key':'39a3af33665a439c9cb2a80532465eb1'})
			}
			let postServiceAuthToken : Subscription = this.http.post(obtenerBaseUrl(configuracionGeneral.base_url,YeminusApis.Security, configuracionGeneral.dev_mode) + 'token', data, headers)
			.subscribe( 
				(responseAuth: {access_token: string, expires_in: number,token_type: string,usuario:string}) => {
				this.guardarAuthToken(responseAuth.access_token, responseAuth.token_type);
				postServiceAuthToken.unsubscribe();
				resolve();
			}, error => {
				console.log('Error al obtener el authtoken->ObtenerAuthToken', error)
				postServiceAuthToken.unsubscribe();
				resolve();
			});
		})
	}

	/**
	 * Se guarda el segundo token que se usará para la conexión con otra Api de .Net 6
	 * @author Bvelez
	 */
	guardarAuthToken(token?: string, tokenType?: string): void {
		// obtengo la información que existe en globals para evitar pérdida de información
		const globals: Globals = this.sessionStorageService.obtenerInfoSesion();
		try {
			if (token && tokenType) {
				globals.authtoken = token;
				globals.authtoken_type = tokenType;
			}
			this.sessionStorageService.modificarInfoSesion(globals);
		} catch (error) {
			UserException('Problemas al actualizar las cookies', error, `${this.guardarAuthToken.name}`);
		}
	}
	/**
	 * Método que obtiene el config resource del proyecto
	 * @author Bvelez
	 * @returns {Observable<IAppSettings>}
	 */
	getDefaultSettings(): Observable<IAppSettings> {
		try {
			return this.http.get('./assets/config-resources/config.json') as Observable<IAppSettings>;
		} catch (error) {
			console.log(`Ocurrió un error al obtener la configuración base`, error, `GlobalsService -> ${this.getDefaultSettings.name}`);
		}
	}



}
